/**
 * PresenceForm — a still, softly-illuminated placeholder shape that
 * suggests Presence without pretending to be the final material.
 *
 * Not a shader. Not animated on its own. Not a decoration. It is
 * deliberately still — every prototype needs a shape of Presence to
 * anchor the composition; that shape must obey the Laws of Presence
 * even in its resting form:
 *   • softly lit from within (radial-gradient)
 *   • never competes for attention (low chroma, no glow beyond the form)
 *   • single continuous body (one element, no seams)
 *
 * Motion is applied *by parents*, not by this component. The material
 * itself is static in isolation, exactly as Law #5 requires: at rest, it
 * simply arrives, and remains.
 *
 * Tokens:
 *   size: pixel diameter (form is roughly circular; parents may distort
 *         via transform/scale within a Presence-preserving motion).
 *   tone: "warm" | "cool" | "neutral" — a whisper of temperature so
 *         Presence can carry mood across contexts, still recognizably
 *         the same substance.
 */
import React from "react";

const TONE_STOPS = {
  neutral: ["rgba(248,246,240,0.98)", "rgba(240,236,228,0.82)", "rgba(230,225,215,0.0)"],
  warm:    ["rgba(252,246,232,0.98)", "rgba(242,232,210,0.82)", "rgba(230,220,200,0.0)"],
  cool:    ["rgba(240,244,248,0.98)", "rgba(220,228,236,0.82)", "rgba(210,220,230,0.0)"],
};

export function PresenceForm({ size = 220, tone = "neutral", className = "", style = {}, testId = "presence-form" }) {
  const stops = TONE_STOPS[tone] || TONE_STOPS.neutral;
  return (
    <div
      data-testid={testId}
      className={`pointer-events-none select-none ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: `radial-gradient(circle at 42% 40%, ${stops[0]} 0%, ${stops[1]} 40%, ${stops[2]} 78%)`,
        // Internal illumination — the highlight is inside the substance,
        // not a shine reflecting from outside.
        boxShadow: "inset 0 0 40px rgba(255,255,255,0.35), inset 6px 8px 22px rgba(255,255,255,0.5)",
        ...style,
      }}
    />
  );
}

export default PresenceForm;
